/**
 * 68.9 — the squad leader sets the squad's own objectives (DIRECTION §0.7, lane D's note; the owner, Oct 10: "bots working
 * together somewhat with a squad leader setting lower level objectives").
 *
 * Within the commander's order (take a point, hold here, go there) the leader's brain picks the squad's **step**, keeps it
 * in its own brain (`ActionBrainState.lead` — 64.8: what a bot knows lives in the state), and the members read it there and
 * follow it, rather than each reading the order alone. Bot-led squads only: when the unit you hold is the leader, you lead
 * (the owner's ruling) and the members follow you as today. Behind `GameConfig.squadLead` (the Lab's row): on by default on
 * the action clock (decision 148, match/setup.ts leadOn).
 *
 * **The leader** (decision 139, the owner: "no leader promotion system at the moment, just respawn of that leader"): the
 * squad's designated unit — its first at the battle's start (`MatchSquad.leader`). While it is down the squad has no
 * leader and its members read the commander's order each for themselves, as before 68.9 (so the leader, whose look sets it
 * apart, is the one worth shooting); when it respawns into its squad it leads again.
 *
 * The steps, worked out again a position period after the last (the leader deliberates on its look slot):
 *   - **approach** — to a staging point on the side of the objective the leader chose (out of the known enemy's sight, by
 *     cover, high, not far round, never through the enemy's side), just outside the hold zone; straight in while nothing
 *     is known. The members follow the leader (their slots, in a formation).
 *   - **bound** — in contact (an enemy it knows of within LEAD.contactPeople of the leader or a member), the squad goes on by halves:
 *     one half walks to posts behind cover a bound ahead (68.15's chooseHoldPosts at the bound point, facing the enemy)
 *     while the other holds where it stands and covers; when the movers are on their posts, or LEAD.boundSeconds pass, the
 *     halves swap. The leader's half covers first.
 *   - **hold** — inside the hold zone (68.15's twelve person heights), posts at the objective facing the enemy (inside the
 *     capture circle, for a point).
 *   - **drill** (66.7, coverMoves: the contact drill on the march) — on the way (not yet in the hold zone), contact: an
 *     enemy it knows of inside the rifle's effective range of any member, or a member hit (or, with suppression, pinned).
 *     The squad goes to the nearest cover posts against the contact (chooseCoverPosts round where it stands, dead ground
 *     counting), fights from them, and after LEAD.drillSeconds the leader calls it (drillCall): withdraw by bounds, away
 *     from the contact, as the units' own judgement says (actionJobs' retreat, for the squad); else, under a move or a take
 *     order (the coordinator's ruling: the owner's "bounding from cover to cover while providing cover", the drill its first
 *     beat), bound on — unless outnumbered (more enemies known near the contact than its living members) or under half
 *     health, when it holds the posts and looks again;
 *     with no such order, the jobs' call (engage — hold and look again; take — bound on). No contact: the march goes on.
 *     With the drill, contact is the drill's (the bound comes by its call).
 * Pure; deterministic math only.
 */
import type { CoverPose, GameState, LeadStep, MatchSquad, PlayerState, SquadOrder, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT } from '../constants';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { dacos, dcos, dsin } from '../core/dmath';
import { personPointOn, personLineOfSight, personSurfaceRadius } from '../core/fineTerrain';
import { segmentCovered } from '../core/bullets';
import { stepToward } from '../core/tactics';
import type { WalkObstacle } from '../core/surfaceWalk';
import type { PersonSet } from '../core/scaleProfile';
import type { ActionParams } from './botParamsAction';
import { chooseHoldPosts, chooseCoverPosts, postHold, bearingSwing, HOLD_FACE_ARC, HOLD_POST_RADIUS_PEOPLE } from './actionPositions';
import { chooseJob } from './actionJobs';
import { localForce } from '../core/localForce';
import { brainFace } from '../core/brainFace';
import { rifleEffectivePeople } from '../core/support';
import { defaultOn } from '../core/defaultOn';
import { squadLeadOf } from '../core/squadMembership';
import { chooseFirst, type ChoiceTrace, type TierOption } from './choice';

/** The leader's rules, in person heights and seconds. */
export const LEAD = {
  /** A known enemy within this of the leader or a member: contact — the squad goes on by bounds. */
  contactPeople: 22,
  /** A bound's point stands this far on from the covering half, toward where the squad is going. */
  boundPeople: 8,
  /** The movers' posts stand within this of the bound's point. */
  boundPostPeople: 3,
  /** A bound ends when its movers are on their posts, or after this long. */
  boundSeconds: 6,
  /** The staging point: this far past the hold zone's edge. */
  stagePeople: 2,
  /** On a post, or at the staging point: within this. */
  arrivePeople: 0.6,
  /** The bearings tried round the objective for the approach. */
  bearings: 12,
  /** A side must beat straight in by this much to be worth the walk round. */
  sideMargin: 0.5,
  /** 66.7 (coverMoves) the contact drill: the posts it takes stand within this of where the squad stands. */
  drillReachPeople: 6,
  /** The drill holds this long before the leader calls it (and a hold's call this long before it looks again). */
  drillSeconds: 4,
  /** A member hit within this long: the squad is taking fire. */
  drillHurtSeconds: 2,
  /** A withdrawal falls back this far from the contact's side of the squad. */
  withdrawPeople: 12,
  /** 66.19: an assault's local read round the contact (people) — against it, the drill; else it keeps bounding. */
  assaultReadPeople: 20,
  /** 415 step 3 (named with step 5): a holding squad holds until its leader is this many zones out (no flicker at the edge). */
  holdHysteresis: 1.5,
  /** A hold's posts keep within this share of the capture radius. */
  captureShare: 0.75,
  /** A hold's posts stand while its face moves less than this (people). */
  holdFacePeople: 2,
  /** A squad of at least this many bounds by halves on contact. */
  boundMembers: 2,
  /** The approach side's score: the threats it looks from (the nearest first), and its terms' weights. */
  approach: {
    threats: 3,
    /** In a threat's sight (per threat). */
    seen: 2,
    /** On a threat's side of the objective: past this cosine, × this weight × the cosine. */
    enemySideCos: 0.5, enemySide: 2,
    /** Cover within this many people (plus its radius), at most `coverMax` counted, × `cover`. */
    coverPeople: 2, coverMax: 2, cover: 0.5,
    /** Height over the objective (per person), and the detour (per person, a penalty). */
    height: 0.3, detour: 0.1,
  },
} as const;

/**
 * The orders a leader turns into steps (68.9): take, hold and go-here, at a point or a place — and, with `bound` (415 step
 * 5b, GameConfig.leadBound), a play's bound (66.4), which the assault rule (66.19) keys on.
 */
export type LeadOrder = Extract<SquadOrder, { kind: 'take' | 'hold' | 'move' | 'bound' }>;
export const isLeadOrder = (o: SquadOrder | null | undefined, bound = false): o is LeadOrder => !!o && (o.kind === 'take' || o.kind === 'hold' || o.kind === 'move' || (bound && o.kind === 'bound'));

/** The key of the order a step serves: its kind and its point, or its place. */
export function leadOrderKey(order: LeadOrder, place: { planetIndex: number; surfaceDir: Vec3 }): string {
  const kind = order.kind === 'hold' && order.ambush ? 'hold!ambush' : order.kind;   // (66.11: an ambush is its own step, armed afresh)
  if (order.pointId !== undefined) return `${kind}|p${order.pointId}`;
  const d = vecNormalize(place.surfaceDir);
  return `${kind}|${place.planetIndex}:${d.x.toFixed(6)},${d.y.toFixed(6)},${d.z.toFixed(6)}`;
}

/** The squad's leader now (decision 139), from core/squadMembership.ts (415 step 1: the squad card reads it too). */
export { squadLeadOf };

/** The hold zone's radius (u): 68.15's — within it, the squad takes its posts. */
export function holdZone(ps: PersonSet): number {
  return 2 * HOLD_POST_RADIUS_PEOPLE * ps.hitboxCenter * 2;
}

export interface LeadArgs {
  state: GameState;
  /** The order's key (leadOrderKey). */
  order: string;
  /** Where the order points; `captureRadius` set for a capture point (the posts stand inside it), null for a place. */
  objective: { planetIndex: number; surfaceDir: Vec3; captureRadius: number | null };
  leader: PlayerState;
  /** The squad's living members the bots drive, on the objective's planet, in the squad's order (the leader first). */
  members: readonly PlayerState[];
  /** The enemies the leader knows of on this planet (surface directions, the most confident first). */
  threats: readonly Vec3[];
  /** Where the squad faces with nothing known: on past the objective (a surface direction). */
  ahead: Vec3;
  /** 66.7 (coverMoves): each member's threat its hold post shelters from (a world point: chooseCoverPosts). */
  threatOf?: ReadonlyMap<number, Vec3>;
  /** 66.7 bound-with-cover (coverMoves): the catalogue's dials (botParamsAction `action.bound`); with `threatOf`, a bound's posts are cover posts. */
  bound?: ActionParams['bound'];
  /** 66.7 (coverMoves): the contact drill on the march (see the module note). */
  drill?: boolean;
  /** 415 #2 (GameConfig.stageByBound): a bound's covering half reaching the staging point stages the squad, not the leader alone. */
  stageByBound?: boolean;
  /** 415 #1(b) (GameConfig.keeperNotLeader): at a capture point the leader keeps the circle only when no other member can. */
  keeperNotLeader?: boolean;
  /** 415 #3(b) (GameConfig.drillCallEarly): the drill's call made by a member for itself, its leader down — a squad of one: it
   * moves on every bound (the halves' split leaves one alone covering forever), and it withdraws only outnumbered (DECISIONS
   * 417's companion: "hurt and outnumbered"; the jobs' own judgement retreats hurt from one). */
  selfCall?: boolean;
  /** 66.11: the order is an ambush hold — its hold step carries `ambush` (armed when the hold begins; sprung by the leader's brain). */
  ambush?: boolean;
  ps: PersonSet;
  obstacles: readonly WalkObstacle[];
  /** The leader's step so far (null: none yet). */
  prev: LeadStep | null;
  /**
   * 415 (ARCHITECTURE §5.52 (6)): the skills that may take the step (ai/skills/*), each a tier option asked in its order
   * after the hold and before the drill — the first that holds gives the step (its `skill` named). None today.
   */
  skills?: readonly TierOption<string, LeadStep>[];
  /** 415 step 5: a sink for the step's choice (which options were asked, which held); off when absent. */
  trace?: { sink: ChoiceTrace; who: number; tick: number };
  tick: number;
  /** The tick's length (s). */
  dt: number;
}

/**
 * 69.103 finding 2 (the coordinator, Nov 3): whether member `id` of `step` is a withdrawal's mover — a bound under the
 * drill's call to withdraw, the member in its moving half. Until it is on its post it keeps its leg: 66.11's fire cover
 * leaves it be (ai/actionBot.ts). An advance's mover, the covering half and a holder are not.
 */
export function withdrawalMover(step: Pick<LeadStep, 'kind' | 'drill' | 'moving'>, id: number): boolean {
  return step.kind === 'bound' && step.drill?.call === 'withdraw' && step.moving.includes(id);
}

/** The squad's step now, from the leader's point of view (see the module note). */
export function leadStep(a: LeadArgs): LeadStep {
  const { state, ps, members, threats, tick } = a;
  const pi = a.objective.planetIndex;
  const planet = state.planets[pi];
  const R = planet.radius;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const O = vecNormalize(a.objective.surfaceDir);
  const arc = (x: Vec3, y: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(x), vecNormalize(y))))) * R;
  const world = (d: Vec3) => vecAdd(planet.position, vecScale(vecNormalize(d), R));
  const prev = a.prev && a.prev.order === a.order && a.prev.planetIndex === pi ? a.prev : null;
  const L = vecNormalize(a.leader.surfaceDir);
  const zone = holdZone(ps);
  const ids = members.map(m => m.id);
  // where the squad faces: the known enemy nearest the objective, else on past it
  const face = threats.length ? threats.reduce((best, t) => (arc(t, O) < arc(best, O) ? t : best)) : vecNormalize(a.ahead);
  const step = (kind: LeadStep['kind'], rest: Partial<LeadStep>): LeadStep => ({ order: a.order, kind, planetIndex: pi, to: O, face, posts: [], moving: [], since: tick, at: tick, staged: true, ...rest });

  // (66.11: an ambush's hold carries it — armed when the hold began, sprung as the leader's brain set it)
  const ambushOf = (): Partial<LeadStep> => a.ambush ? { ambush: prev?.kind === 'hold' && prev.ambush ? { ...prev.ambush } : { armed: tick } } : {};
  // hold: in the zone (a little past it once holding, so the squad does not flicker at the edge)
  const dLead = arc(L, O);
  const holdStep = (): LeadStep => {
    if (a.threatOf) {
      // 66.7 (coverMoves): a post per member against its own threat (chooseCoverPosts); they stand while the squad is the
      // same and no member's threat has swung past HOLD_FACE_ARC; at a capture point one keeps the circle
      const was = prev?.kind === 'hold' ? prev.threats : undefined;
      const keepSame = !!was && prev!.posts.length === ids.length && ids.every(id => {
        const x = was.find(([w]) => w === id)?.[1], y = a.threatOf!.get(id);
        return !!x && !!y && prev!.posts.some(([p]) => p === id) && bearingSwing(O, vecSub(x, planet.position), vecSub(y, planet.position)) < HOLD_FACE_ARC;
      });
      const posts = keepSame ? prev!.posts : [...chooseCoverPosts({ state, planetIndex: pi, place: O, ps, obstacles: a.obstacles, members, threatOf: a.threatOf,
        ...(a.objective.captureRadius !== null ? { keep: LEAD.captureShare * a.objective.captureRadius, ...(a.keeperNotLeader ? { notKeeper: a.leader.id } : {}) } : {}),
        ...(prev?.kind === 'hold' ? { current: new Map(prev.posts) } : {}) })];
      // (how each stands at its post: the view and the gun kept — chosen with the posts)
      const holds = keepSame && prev!.stances ? null : posts.map(([id, d]) => [id, postHold(state, pi, d, a.threatOf!.get(id) ?? world(face), ps, a.obstacles)] as const);
      // (lean step 2: a lean post moved to its cover's edge is the post it takes)
      if (holds) holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });
      const stances = holds ? holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']) : prev!.stances!;
      const poses = holds ? holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]) : prev!.poses ?? [];
      return step('hold', { posts, threats: keepSame ? prev!.threats! : [...a.threatOf], stances, poses, since: keepSame ? prev!.since : tick, ...ambushOf() });
    }
    const same = prev?.kind === 'hold' && prev.posts.length === ids.length && prev.posts.every(([id]) => ids.includes(id)) && arc(prev.face, face) < LEAD.holdFacePeople * person;
    const posts = same ? prev!.posts : [...chooseHoldPosts({ state, planetIndex: pi, place: O, facing: world(face), ps, obstacles: a.obstacles, members,
      ...(a.objective.captureRadius !== null ? { reach: LEAD.captureShare * a.objective.captureRadius } : {}),
      ...(prev?.kind === 'hold' ? { current: new Map(prev.posts) } : {}) })];   // (66.7 b: each keeps its post)
    return step('hold', { posts, since: same ? prev!.since : tick, ...ambushOf() });
  };

  // the way in: the staging point while it stands (prev.to, not yet staged), else the objective; a side is chosen once
  // something is known (and again if the squad lost its step) — worked out once, when a step outside the hold asks
  let wayIn: { to: Vec3; staged: boolean } | null = null;
  const way = (): { to: Vec3; staged: boolean } => {
    if (wayIn) return wayIn;
    let to = O, staged = prev?.staged ?? false;
    const sideOpen = !prev || (!prev.staged && arc(prev.to, O) < 1e-6 * R);
    if (prev && !sideOpen) { to = prev.to; staged = prev.staged; }
    if (sideOpen && threats.length) {
      const s = approachSide(a, O, L, zone, person, arc);
      if (s) { to = s; staged = false; } else staged = true;
    }
    if (!staged && arc(to, O) > 1e-6 * R) {
      // reached the staging point (the leader, or a bound's covering half): straight in from here
      // (415 #2, stageByBound: the covering half too — its centroid within a bound's reach of the stage, the offset and the
      // cover search round the bound's point; off, the leader alone, which at a cover post off the bound's line came within
      // 2 arrivePeople of the stage too rarely: drill 1's take squads bounded round the 12.9-people ring for 10–20 s)
      const covering = a.stageByBound && a.bound && prev?.kind === 'bound' ? members.filter(m => !prev.moving.includes(m.id)) : [];
      const coverAt = covering.length ? centroid(covering.map(m => m.surfaceDir)) : null;
      if (arc(L, to) < 2 * LEAD.arrivePeople * person || (coverAt && arc(coverAt, to) <= (a.bound!.offsetPeople + a.bound!.coverReachPeople) * person)) { to = O; staged = true; }
    }
    return (wayIn = { to, staged });
  };

  // bound: in contact, by halves (toward `goal`: the way in, or a withdrawal's fall-back point)
  const bound = (goal: Vec3, drill?: LeadStep['drill']): LeadStep => {
    const alive = (id: number) => members.find(m => m.id === id);
    if (prev?.kind === 'bound') {
      const movers = prev.moving.filter(id => ids.includes(id));
      const postOf = (id: number) => prev.posts.find(([x]) => x === id)?.[1] ?? null;
      const onPosts = movers.every(id => { const p = postOf(id), m = alive(id); return !p || !m || arc(m.surfaceDir, p) < LEAD.arrivePeople * person; });
      const due = (tick - prev.since) * a.dt >= LEAD.boundSeconds;
      if (movers.length && !onPosts && !due) return { ...prev, to: goal, staged: way().staged, face, at: tick, posts: prev.posts.filter(([id]) => ids.includes(id)), moving: movers, ...(drill ? { drill } : {}) };
    }
    // a new bound: the half that covered moves (the leader's half covers the first)
    const odd = members.filter((_, i) => i % 2 === 1).map(m => m.id), even = members.filter((_, i) => i % 2 === 0).map(m => m.id);
    let moving = prev?.kind === 'bound' ? ids.filter(id => !prev.moving.includes(id)) : odd;
    if (moving.length === 0 || moving.length === ids.length) moving = prev?.kind === 'bound' && prev.moving.some(id => odd.includes(id)) ? even : odd;
    // (69.103's finding, a squad of one: the halves' split leaves its odd half empty and the reset falls back to it — nobody
    // moved, a lone leader's withdrawal stood where it was called; alone, it moves every bound)
    const lone = members.length === 1;
    if (lone) moving = ids;
    const covering = members.filter(m => !moving.includes(m.id));
    const from = covering.length ? centroid(covering.map(m => m.surfaceDir)) : L;
    let B = stepToward(from, goal, LEAD.boundPeople * person, R);
    // 66.7 bound-with-cover, the offset bound (coverMoves): the bound's point stands bound.offsetPeople to the side of the
    // covering half's line to the threat, on the side the movers are on — the halves leapfrog on parallel tracks and the
    // covering half's fire has a lane past them (straight ahead, the movers stood in it and held its fire)
    if (a.threatOf && a.bound && a.bound.offsetPeople > 0) {
      const n = vecNormalize(from), toThreat = vecSub(vecNormalize(face), vecScale(n, vecDot(vecNormalize(face), n)));
      if (vecLength(toThreat) > 1e-12) {
        const fwd = vecNormalize(toThreat), side = vecNormalize(vecCross(n, fwd));
        const moversAt = centroid(members.filter(m => moving.includes(m.id)).map(m => m.surfaceDir));
        let sgn = vecDot(vecSub(moversAt, n), side) >= 0 ? 1 : -1;
        if (Math.abs(vecDot(vecSub(moversAt, n), side)) * R < 0.25 * person) sgn = moving.includes(members[1]?.id ?? -1) ? 1 : -1;   // (on the line: by the squad's order)
        const b0 = vecNormalize(B), th = (a.bound.offsetPeople * person) / R;
        const across = vecNormalize(vecSub(side, vecScale(b0, vecDot(side, b0))));
        B = vecNormalize(vecAdd(vecScale(b0, dcos(th)), vecScale(across, sgn * dsin(th))));
      }
    }
    const movers = members.filter(m => moving.includes(m.id));
    if (a.threatOf && a.bound) {
      // 66.7 bound-with-cover (coverMoves): the movers' stops are cover posts round the bound's point, each against its own
      // threat as a hold's are; the half that covers keeps the posts it bounded to, or (the first bound) settles into the
      // cover nearest it; both take their posts' stances and poses — the covering half fires from cover (actionBot)
      const threatOf = a.threatOf;
      const moverPosts = [...chooseCoverPosts({ state, planetIndex: pi, place: B, ps, obstacles: a.obstacles, members: movers, threatOf, reach: a.bound.coverReachPeople * person })];
      const had = prev?.kind === 'bound' ? new Map(prev.posts) : null;
      const settled = covering.filter(m => had?.has(m.id));
      const settle = covering.filter(m => !had?.has(m.id));
      const coverPosts: Array<[number, Vec3]> = [
        ...settled.map(m => [m.id, had!.get(m.id)!] as [number, Vec3]),
        ...(settle.length ? [...chooseCoverPosts({ state, planetIndex: pi, place: centroid(settle.map(m => m.surfaceDir)), ps, obstacles: a.obstacles, members: settle, threatOf, reach: a.bound.settlePeople * person })] : []),
      ];
      const posts = [...moverPosts, ...coverPosts];
      const holds = posts.map(([id, d]) => [id, postHold(state, pi, d, threatOf.get(id) ?? world(face), ps, a.obstacles)] as const);
      holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });   // (lean step 2: a lean post at its cover's edge)
      const stances = holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']);
      const poses = holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]);
      return step('bound', { to: goal, staged: way().staged, posts, moving: movers.map(m => m.id), threats: [...threatOf], stances, poses, ...(drill ? { drill } : {}) });
    }
    const posts = [...chooseHoldPosts({ state, planetIndex: pi, place: B, facing: world(face), ps, obstacles: a.obstacles, members: movers, reach: LEAD.boundPostPeople * person })];
    return step('bound', { to: goal, staged: way().staged, posts, moving: movers.map(m => m.id), ...(drill ? { drill } : {}) });
  };

  // 66.7 (coverMoves): the contact drill on the march — contact takes the nearest cover first, and the leader's call. Its
  // read, worked out once when a drill option asks: the contact near, a member hit, the call under way, the contact's point
  let drillNow: { near: Vec3[]; hit: boolean; was: LeadStep['drill']; mid: Vec3; c: Vec3 } | null = null;
  const dr = () => {
    if (drillNow) return drillNow;
    const reach = rifleEffectivePeople() * person;
    const near = threats.filter(t => members.some(m => arc(t, m.surfaceDir) <= reach));
    const hit = members.some(m => {
      const h = brainFace(state, m.id)?.hurtAt;   // (§5.16 step 2: a brain read through its public face)
      return (h !== undefined && (tick - h) * a.dt < LEAD.drillHurtSeconds) || (defaultOn(state.config, 'suppression') && (m.suppressed ?? 0) > 0);
    });
    const was = prev?.drill;
    const mid = centroid(members.map(m => m.surfaceDir));
    const c = near.length ? world(near.reduce((x, y) => (arc(x, mid) <= arc(y, mid) ? x : y))) : was?.contact ?? world(face);
    return (drillNow = { near, hit, was, mid, c });
  };
  const drillPosts = (): LeadStep => {
    const { mid, c } = dr();
    const threatOf = new Map(members.map(m => [m.id, c] as const));
    const posts = [...chooseCoverPosts({ state, planetIndex: pi, place: mid, ps, obstacles: a.obstacles, members, threatOf, reach: LEAD.drillReachPeople * person })];
    const holds = posts.map(([id, d]) => [id, postHold(state, pi, d, c, ps, a.obstacles)] as const);
    holds.forEach(([id, h], i) => { if (h.at) posts[i] = [id, h.at]; });
    const stances = holds.map(([id, h]) => [id, h.stance] as [number, 'crouch' | 'stand' | 'prone' | 'rise']);
    const poses = holds.filter(([, h]) => h.pose).map(([id, h]) => [id, h.pose!] as [number, { pose: CoverPose; side?: 1 | -1; step?: number }]);
    return step('drill', { to: way().to, staged: way().staged, posts, threats: posts.map(([id]) => [id, c] as [number, Vec3]), stances, poses, drill: { contact: c, began: tick } });
  };
  // 66.19 (the owner: fix the attack, then cover on): an assault (a take, a play's bound) keeps bounding under fire — the
  // covering half fires on the group it knows, the rest move to the next cover toward the point — and goes to ground only
  // when the local read round the contact is against it (its living and ours there fewer than their believed number)
  const orderKind = a.order.split('|')[0];
  const assaultHolds = (): boolean => {
    if (orderKind !== 'take' && orderKind !== 'bound') return false;
    const { was, c } = dr();
    const lf = localForce(state, members[0].teamIndex, { planetIndex: pi, surfaceDir: vecNormalize(vecSub(c, planet.position)) }, LEAD.assaultReadPeople, tick);
    // (their number: the side's beliefs there, or what the leader itself knows round the contact, whichever is more)
    const known = threats.filter(t => arc(t, c) <= LEAD.assaultReadPeople * person).length;
    const against = (members.length + lf.friends) < Math.max(lf.enemies, known);
    return !against && was?.call !== 'withdraw';
  };
  // the leader's call (the units' own job judgement, drillCall): hold the posts, bound on, or withdraw — back from the
  // contact, along the line from it through the squad
  const drillCalled = (): LeadStep => {
    const { near, was, mid, c } = dr();
    const called = drillCall(members, near.length, arc(L, O) / ps.k, orderKind === 'move' || orderKind === 'take');
    const call = a.selfCall && called === 'withdraw' && near.length <= members.length ? 'hold' : called;   // (415 #3(b): alone, only outnumbered)
    if (call === 'hold') return { ...prev!, at: tick, drill: { ...was!, contact: c, call, calledAt: tick } };
    if (call === 'bound') return bound(way().to, { ...was!, contact: c, call, calledAt: tick });
    return withdrawn(was!);
  };
  const withdrawn = (was: NonNullable<LeadStep['drill']>): LeadStep => {
    const { mid, c } = dr();
    const away = vecSub(mid, vecScale(vecNormalize(vecSub(c, planet.position)), 1));
    const back = stepToward(mid, vecNormalize(vecAdd(mid, away)), LEAD.withdrawPeople * person, R);
    return bound(back, { ...was, contact: c, call: 'withdraw', calledAt: tick });
  };
  // 415 #3(b) (selfCall): alone and outnumbered, the call made at contact — no posts and no look first (the switch's name:
  // the call comes early; the coordinator after the first build, whose look toward the cover took it closer first)
  const selfCallNow = (): boolean => {
    if (!a.selfCall || members.length !== 1 || dr().was?.calledAt !== undefined || dr().near.length <= members.length) return false;
    return drillCall(members, dr().near.length, arc(L, O) / ps.k, orderKind === 'move' || orderKind === 'take') === 'withdraw';
  };
  // (68.9, lane B's finding with the fire lanes: contact is the squad's — a known enemy within reach of the leader or of
  // any member under the step; measured from the leader alone, a leader back from its respawn 27 people off walked in for
  // 3 s while its members, 20 people off, fought with no step)
  const contact = (): boolean => threats.some(t => arc(t, L) < LEAD.contactPeople * person || members.some(m => arc(t, m.surfaceDir) < LEAD.contactPeople * person));
  const approach = (): LeadStep => step('approach', { to: way().to, staged: way().staged, since: prev?.kind === 'approach' ? prev.since : tick });

  // 415 step 5 (ARCHITECTURE §5.52 (3)): the step as a choice in tier order — the first option that holds gives it, the
  // rest are never asked (the chain as it was, option by option; ai/choice.ts chooseFirst)
  const drillOn = !!a.drill;
  return chooseFirst<string, LeadStep>([
    // hold: in the zone (a little past it once holding, so the squad does not flicker at the edge)
    { kind: 'hold', when: () => dLead <= zone || (prev?.kind === 'hold' && dLead <= LEAD.holdHysteresis * zone), give: holdStep },
    ...(a.skills ?? []),
    // (the drill, with coverMoves) no contact: the march goes on
    { kind: 'march', when: () => drillOn && dr().near.length === 0 && !dr().hit, give: approach },
    { kind: 'assault', when: () => drillOn && assaultHolds(), give: () => { const { was, c } = dr(); return bound(way().to, { ...(was ?? { began: tick }), contact: c, call: 'bound', calledAt: was?.calledAt ?? tick }); } },
    // the call under way: a bound on, or a withdrawal (to its fall-back point; there, a fresh drill)
    { kind: 'boundOn', when: () => drillOn && prev?.kind === 'bound' && dr().was?.call === 'bound', give: () => bound(way().to, { ...dr().was!, contact: dr().c }) },
    { kind: 'withdrawing', when: () => drillOn && prev?.kind === 'bound' && dr().was?.call === 'withdraw',
      give: () => arc(dr().mid, prev!.to) < 2 * LEAD.arrivePeople * person ? drillPosts() : bound(prev!.to, { ...dr().was!, contact: dr().c }) },
    { kind: 'selfCallNow', when: () => drillOn && selfCallNow(), give: () => withdrawn(dr().was ?? { contact: dr().c, began: tick }) },
    { kind: 'drillPosts', when: () => drillOn && (prev?.kind !== 'drill' || !dr().was), give: drillPosts },
    // the drill: the posts stand until the leader has looked (and a hold's call until it looks again)
    { kind: 'drillStand', when: () => drillOn && (tick - (dr().was!.calledAt ?? dr().was!.began)) * a.dt < LEAD.drillSeconds, give: () => ({ ...prev!, at: tick, drill: { ...dr().was!, contact: dr().c } }) },
    { kind: 'drillCall', when: () => drillOn, give: drillCalled },
    // (no drill) contact: bound by halves
    { kind: 'contactBound', when: () => contact() && members.length >= LEAD.boundMembers, give: () => bound(way().to) },
    { kind: 'approach', when: () => true, give: approach },
  ], { name: 'leadStep', ...(a.trace ? { trace: a.trace } : {}) });
}

/**
 * The side to come in from: a staging point just past the hold zone, on the bearing round the objective that scores best —
 * out of each known enemy's sight, not on the enemy's side of the objective (that is coming in through them), by cover,
 * high, and not far round. Null when straight in (the bearing toward the leader) is as good, or the leader is already close.
 */
function approachSide(a: LeadArgs, O: Vec3, L: Vec3, zone: number, person: number, arc: (x: Vec3, y: Vec3) => number): Vec3 | null {
  const { state, ps } = a;
  const planet = state.planets[a.objective.planetIndex];
  const R = planet.radius;
  const ring = zone + LEAD.stagePeople * person;
  if (arc(L, O) <= ring + person) return null;
  const tangentTo = (d: Vec3): Vec3 | null => { const t = vecSub(vecNormalize(d), vecScale(O, vecDot(vecNormalize(d), O))); return vecLength(t) < 1e-12 ? null : vecNormalize(t); };
  const toLeader = tangentTo(L);
  if (!toLeader) return null;
  const across = vecNormalize(vecCross(O, toLeader));
  const theta = ring / R;
  const groundO = personSurfaceRadius(planet, O, ps);
  const eyes = a.threats.slice(0, LEAD.approach.threats).map(t => ({ eye: personPointOn(planet, t, ps, ps.losEyeHeight), side: tangentTo(t) }));
  // (the cover round a point on the ring: every point is `ring` from O, so an obstacle farther off the ring than the count's
  // reach is near none of them — arc(o, S) ≥ |arc(o, O) − ring| — and the count reads only those near it, a person's margin
  // over the rounding: the same count at a twelfth of the arcs; 69.67, it was 94 % of the call, 4–12 ms a tick)
  const reach = LEAD.approach.coverPeople * person;
  const nearRing = a.obstacles.filter(o => Math.abs(arc(o.dir, O) - ring) < reach + o.radius + person);
  const score = (S: Vec3): number => {
    let s = 0;
    const body = personPointOn(planet, S, ps, ps.hitboxCenter);
    const out = tangentTo(S)!;
    for (const e of eyes) {
      if (personLineOfSight(e.eye, body, state) && !segmentCovered(e.eye, body, state, undefined, ps.coverSampleStep)) s -= LEAD.approach.seen;   // in its sight
      if (e.side) { const c = vecDot(out, e.side); if (c > LEAD.approach.enemySideCos) s -= LEAD.approach.enemySide * c; }   // on the enemy's side of the objective
    }
    let cover = 0;
    for (const o of nearRing) if (arc(o.dir, S) < reach + o.radius) cover++;
    s += LEAD.approach.cover * Math.min(LEAD.approach.coverMax, cover);
    s += LEAD.approach.height * (personSurfaceRadius(planet, S, ps) - groundO) / person;
    s -= LEAD.approach.detour * (arc(L, S) + arc(S, O) - arc(L, O)) / person;
    return s;
  };
  const at = (bearing: number): Vec3 => {
    const t = vecAdd(vecScale(toLeader, dcos(bearing)), vecScale(across, dsin(bearing)));
    return vecNormalize(vecAdd(vecScale(O, dcos(theta)), vecScale(t, dsin(theta))));
  };
  const straight = score(at(0));
  let best: Vec3 | null = null, bestScore = straight + LEAD.sideMargin;
  for (let k = 1; k < LEAD.bearings; k++) {
    const S = at((2 * Math.PI * k) / LEAD.bearings);
    const s = score(S);
    if (s > bestScore) { best = S; bestScore = s; }
  }
  return best;
}

/**
 * 66.7: the leader's call after a contact drill. Withdraw as the units' own judgement says (actionJobs: the scores every
 * bot's job is chosen by, with the default weights, for the squad — its mean health, the enemies it knows near against its
 * members, the objective's pull, `takeDist` in person lengths as the jobs read them: retreat). Else, `ordered` (a move or a
 * take: the coordinator's ruling), bound on — unless outnumbered (strictly more enemies known near the contact than its
 * living members: three bound on against one, two or three, and hold against four or more — mass at a held point is the
 * commander's to bring, not the drill's) or its mean health under half, when it holds the drill's posts and looks again.
 * Unordered, the jobs' call: engage holds, take (or anything else) bounds on.
 */
export function drillCall(members: readonly PlayerState[], enemiesNear: number, takeDist: number, ordered = false): 'hold' | 'bound' | 'withdraw' {
  const health = members.reduce((s, m) => s + Math.max(0, Math.min(1, m.health / 100)), 0) / Math.max(1, members.length);
  const job = chooseJob({ hasTarget: true, health, threats: enemiesNear, alliesNear: members.length - 1, takeDist, holdDist: null, lastKnownAge: 0, order: 'take', current: null });
  if (job === 'retreat') return 'withdraw';
  if (ordered) return enemiesNear > members.length || health < DRILL_HOLD_HEALTH ? 'hold' : 'bound';
  return job === 'engage' ? 'hold' : 'bound';
}
/** An ordered squad under this mean health holds after its drill rather than bound on. */
const DRILL_HOLD_HEALTH = 0.5;

function centroid(dirs: readonly Vec3[]): Vec3 {
  let s: Vec3 = { x: 0, y: 0, z: 0 };
  for (const d of dirs) s = vecAdd(s, vecNormalize(d));
  return vecNormalize(s);
}
